import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { CampaignMultiSelectSummaryField } from '@fmnts/api/next';
import { logger } from '@fmnts/common/log';
import { FmntsIconsModule } from '@fmnts/components/icons';
import { isDefined, isString } from '@fmnts/core';
import {
  AbstractDeclarativeFieldComponent,
  FormDeclarationError,
  SelectInputItem,
  ensureTemplate,
  isFormSelectField,
  isTemplateExpression,
  splitTemplate,
} from '@fmnts/declarative-forms';
import { I18nModule } from '@fmnts/i18n';
import { faSquare, faSquareCheck } from '@fortawesome/pro-regular-svg-icons';
import {
  Observable,
  filter,
  map,
  mergeMap,
  of,
  startWith,
  switchMap,
  throwError,
} from 'rxjs';

interface SummarySelectInputItem extends SelectInputItem {
  checked: boolean;
}

/**
 * Summary field for displaying checkbox groups
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'next-checkbox-group-summary-field',
  templateUrl: './checkbox-group-summary-field.component.html',
  imports: [I18nModule, AsyncPipe, FmntsIconsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckboxGroupSummaryFieldComponent
  extends AbstractDeclarativeFieldComponent<CampaignMultiSelectSummaryField>
  implements OnInit
{
  private readonly _log = logger({
    name: 'CheckboxGroupSummaryFieldComponent',
    category: 'declarative-forms.field',
  });

  /** Icon to use, if state is checked */
  protected readonly iconChecked = faSquareCheck;
  /** Icon to use, if state is unchecked */
  protected readonly iconUnchecked = faSquare;

  /** Checkboxes items for summary-view */
  protected items$!: Observable<SummarySelectInputItem[]>;

  ngOnInit(): void {
    const { defaultValue } = this.field.widgetOptions;

    if (!isString(defaultValue) && isDefined(defaultValue)) {
      this.items$ = this._getItemsFromField$(defaultValue);
    }

    if (isString(defaultValue)) {
      this.items$ = of(defaultValue).pipe(
        // Ensure that for any emitted value that references a field
        // that it is transformed into templating form of `{{some-field-key}}`
        map((v) => ensureTemplate(v, this.declaration)),
        map(splitTemplate),
        mergeMap((parts) => {
          // We only support single template expressions, and do not
          // interpolate over literals + expressions.
          const p = parts[0];

          if (!isTemplateExpression(p)) {
            return throwError(
              () =>
                new FormDeclarationError({
                  message: 'Not a valid expression',
                }),
            );
          }

          return of(p.expression);
        }),
        filter(isDefined),
        switchMap((fieldReference) =>
          this._getItemsFromReference$(fieldReference),
        ),
      );
    }
  }

  /**
   * Map items from form-declaration to checkbox-summary
   *
   * Will also map all items as `checked`, since we currently don't
   * support sending any property regarding checked/unchecked.
   *
   * @param defaultValue
   * @returns
   */
  private _getItemsFromField$(
    defaultValue: SelectInputItem[],
  ): Observable<SummarySelectInputItem[]> {
    return of(defaultValue.map((value) => ({ ...value, checked: true })));
  }

  /**
   * Map items from a field-reference to checkbox-summary
   * Uses formValues to format checkboxes as checked/unchecked
   *
   * @param fieldReference reference to field,
   * e.g. `field-key`
   */
  private _getItemsFromReference$(
    fieldReference: string,
  ): Observable<SummarySelectInputItem[]> {
    const otherField = this.declaration.findByKey(
      fieldReference,
      isFormSelectField,
    );

    if (!otherField) {
      return throwError(
        () =>
          new FormDeclarationError({
            message: `Cannot find referenced field: {{${fieldReference}}}`,
          }),
      );
    }

    return this.form.flatValueChanges.pipe(
      startWith(this.form.flatValue),
      map((v) => (v?.[fieldReference] as (number | string)[]) || []),
      map((fieldValues) =>
        otherField.widgetOptions.items.map((value) =>
          fieldValues.includes(value.value)
            ? { ...value, checked: true }
            : { ...value, checked: false },
        ),
      ),
    );
  }
}
